import { z } from 'zod';

/**
 * Referência ao NetSuite (ADR 0073). O Loot ERP é a base central; o NetSuite é outro sistema em que
 * a LOOT também registra pedidos e lançamentos. Não há integração nem vínculo: cada registro só
 * guarda, quando houver, **um** campo opcional com a referência de lá.
 * - **Cadastros** (empresas, centros, classificações, bens, contas bancárias...): **ID Interno do
 *   NetSuite**. É a chave dos templates de importação futuros.
 * - **Lançamentos** (notas, títulos, pagamentos...): **Nº de Registro do NetSuite**.
 * O valor fica como veio (zeros à esquerda e letras), só sem espaços nas pontas. Trocar o valor
 * encerra o anterior, que fica no histórico. Os fornecedores mantêm as referências pelo próprio
 * cadastro (ADR 0014).
 */

/** Tipos de registro que guardam a referência na tabela única de referências (ADR 0013). */
export const NETSUITE_ENTITY_TYPES = [
  'legal_entity',
  'cost_center',
  'department',
  'classification',
  'asset',
  'treasury_bank_account',
  'finance_payable',
  'finance_payment',
  'treasury_movement',
  'treasury_transfer',
  'treasury_adjustment',
  'treasury_receipt',
] as const;
export type NetsuiteEntityType = (typeof NETSUITE_ENTITY_TYPES)[number];

export type NetsuiteReferenceKind = 'internal_id' | 'record_number';

/** Cadastro guarda o ID Interno; lançamento guarda o Nº de Registro. */
export const NETSUITE_REFERENCE_KIND: Record<NetsuiteEntityType, NetsuiteReferenceKind> = {
  legal_entity: 'internal_id',
  cost_center: 'internal_id',
  department: 'internal_id',
  classification: 'internal_id',
  asset: 'internal_id',
  treasury_bank_account: 'internal_id',
  finance_payable: 'record_number',
  finance_payment: 'record_number',
  treasury_movement: 'record_number',
  treasury_transfer: 'record_number',
  treasury_adjustment: 'record_number',
  treasury_receipt: 'record_number',
};

/** Valor opcional: vazio apaga a referência (o anterior fica no histórico). */
export const netsuiteReferenceInput = z.object({
  code: z
    .string()
    .trim()
    .max(100)
    .transform((v) => (v === '' ? null : v))
    .nullable(),
});
export type NetsuiteReferenceInput = z.infer<typeof netsuiteReferenceInput>;
